import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, IconButton, Tooltip, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Stack, Card,
  CardContent, Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Add as AddIcon, Brush as BrushIcon, ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { apiService } from '../../services/api';

// Ciclo de un arte: solicitado -> en diseño -> listo -> agendado -> publicado
const COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'requested', label: 'Solicitado', color: '#9e9e9e' },
  { key: 'in_design', label: 'En diseño', color: '#ed6c02' },
  { key: 'ready', label: 'Listo', color: '#0288d1' },
  { key: 'scheduled', label: 'Agendado', color: '#1976d2' },
  { key: 'published', label: 'Publicado', color: '#2e7d32' },
];
const NEXT: Record<string, string> = { requested: 'in_design', in_design: 'ready', ready: 'scheduled' };
const CHANNELS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram_story', label: 'Instagram Story' },
  { value: 'facebook_story', label: 'Facebook Story' },
];

interface Post {
  _id: string; channel: string; type: string; caption?: string; mediaUrls?: string[];
  status: string; arte?: { brief?: string; assetUrls?: string[] };
}

const ArtesPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: 'instagram', brief: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getSocialPosts();
      setPosts((res.data as Post[]) || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error cargando artes');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const createArte = async () => {
    setSaving(true); setError(null);
    try {
      await apiService.createSocialPost({
        channel: form.channel, type: 'image', status: 'requested', arte: { brief: form.brief },
      });
      setOpen(false); setForm({ channel: 'instagram', brief: '' });
      await fetchPosts();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error creando el arte');
    } finally { setSaving(false); }
  };

  const moveNext = async (p: Post) => {
    const next = NEXT[p.status];
    if (!next) return;
    setBusyId(p._id);
    try { await apiService.updateSocialPost(p._id, { status: next }); await fetchPosts(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Error moviendo el arte'); }
    finally { setBusyId(null); }
  };

  const byStatus = (key: string) => posts.filter((p) => p.status === key);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <BrushIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>Artes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Solicitar arte</Button>
        <Tooltip title="Refrescar"><IconButton onClick={fetchPosts}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={1.5}>
          {COLUMNS.map((col) => {
            const items = byStatus(col.key);
            return (
              <Grid key={col.key} size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.default', minHeight: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: col.color }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{col.label}</Typography>
                    <Chip size="small" label={items.length} sx={{ ml: 'auto' }} />
                  </Box>
                  <Stack spacing={1}>
                    {items.map((p) => (
                      <Card key={p._id} variant="outlined">
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          {(p.mediaUrls?.[0] || p.arte?.assetUrls?.[0]) && (
                            <Avatar variant="rounded" src={p.mediaUrls?.[0] || p.arte?.assetUrls?.[0]} sx={{ width: '100%', height: 80, mb: 1 }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.channel}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {p.arte?.brief || p.caption || '(sin brief)'}
                          </Typography>
                          {NEXT[p.status] && (
                            <Button size="small" fullWidth sx={{ mt: 1 }} endIcon={busyId === p._id ? <CircularProgress size={14} /> : <ArrowForwardIcon />}
                              disabled={busyId === p._id} onClick={() => moveNext(p)}>
                              Mover a {COLUMNS.find((c) => c.key === NEXT[p.status])?.label}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                    {items.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>—</Typography>}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Solicitar arte</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select fullWidth label="Canal" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
              {CHANNELS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Brief (qué arte necesitas)" multiline minRows={4} fullWidth
              placeholder="Ej: Reel con estadística de robos de vehículos en RD + CTA a WhatsApp"
              value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cerrar</Button>
          <Button variant="contained" onClick={createArte} disabled={!form.brief.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArtesPage;
